import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Droplets, Clock, ShieldCheck, Star, Cpu } from 'lucide-react';
import { Section, Head, Card, Index, Eyebrow, Lead } from '../components/common/Primitives';
import { statistics, features, processSteps } from '../data/site';
import { systemMeta, layerGlance } from '../data/system';
import heroImage from '../assets/tech/telemetry-greenhouse.jpg';
import kitchenTable from '../assets/farm/kitchen-table.jpg';

const ICONS = { droplet: Droplets, clock: Clock, shield: ShieldCheck, star: Star };

const Home = () => (
  <div>
    {/* ---------- Hero ---------- */}
    <section className="relative bg-ink overflow-hidden">
      <img
        src={heroImage}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-br from-ink/95 via-ink/80 to-ink/60"
      />
      <div className="relative container mx-auto px-5 md:px-8 pt-36 pb-20 md:pt-48 md:pb-28">
        <div className="max-w-3xl">
          <Eyebrow tone="dark">Jaipur · 500 families only</Eyebrow>
          <h1 className="mt-6 font-display font-semibold text-paper text-[2.75rem] leading-[1.05] md:text-[4.25rem]">
            Food grown under
            <br />
            <span className="text-lime">measured</span> conditions.
          </h1>
          <p className="mt-7 text-paper/70 text-lg md:text-xl leading-relaxed max-w-2xl">
            Pesticide-free greens, herbs and vegetables from a hydroponic and aeroponic greenhouse
            that instruments every tray. Harvested in the morning, on your table by dinner.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/farm-tech"
              className="inline-flex items-center gap-2 rounded-full bg-lime text-ink px-7 py-3.5 font-semibold hover:bg-paper transition-colors"
            >
              See how it is grown <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/membership"
              className="inline-flex items-center gap-2 rounded-full ring-1 ring-white/25 px-7 py-3.5 font-medium text-paper hover:ring-lime hover:text-lime transition-colors"
            >
              Request an invitation
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* ---------- Stats ---------- */}
    <Section tone="soil" tight>
      <dl className="grid grid-cols-2 lg:grid-cols-4 gap-8">
        {statistics.map((s) => (
          <div key={s.label}>
            <dt className="font-display font-semibold text-[2.5rem] md:text-[3rem] leading-none text-lime tabular-nums">
              {s.value}
              <span className="text-[1.4rem] align-top ml-0.5 text-lime/70">{s.unit}</span>
            </dt>
            <dd className="mt-3 text-paper/55 text-[14px] leading-snug">{s.label}</dd>
          </div>
        ))}
      </dl>
    </Section>

    {/* ---------- Why different ---------- */}
    <Section tone="paper">
      <Head
        eyebrow="Why it is different"
        title="Nothing here is left to chance"
        lead="Every claim below is a consequence of growing indoors, without soil, under continuous measurement."
        center
      />
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {features.map((f) => {
          const Icon = ICONS[f.icon] || Star;
          return (
            <Card key={f.title} className="p-7">
              <span className="w-12 h-12 rounded-xl grid place-items-center bg-forest/10 text-forest">
                <Icon className="w-6 h-6" />
              </span>
              <h3 className="mt-5 font-display font-semibold text-[1.15rem] text-ink leading-snug">
                {f.title}
              </h3>
              <p className="mt-2.5 text-soil/70 text-[14.5px] leading-relaxed">{f.description}</p>
            </Card>
          );
        })}
      </div>
    </Section>

    {/* ---------- Farm tech, front and centre ---------- */}
    <Section tone="ink" className="fieldgrid">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <Eyebrow tone="dark">
            <Cpu className="w-3.5 h-3.5" /> Farm Tech
          </Eyebrow>
          <h2 className="mt-5 font-display font-semibold text-[2.1rem] md:text-[3rem] leading-[1.1] text-paper">
            A closed loop between the soil and the valve
          </h2>
          <Lead tone="dark" className="mt-6">
            {systemMeta.tagline}
          </Lead>
          <p className="mt-6 text-paper/55 text-[15px] leading-relaxed">
            A model can recommend and an assistant can explain, but the only things permitted to
            dose fertilizer are a deterministic decider and a gate it cannot influence.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link
              to="/farm-tech/system"
              className="inline-flex items-center gap-2 rounded-full bg-lime text-ink px-6 py-3 font-semibold hover:bg-paper transition-colors"
            >
              Explore the system <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/farm-tech/components"
              className="inline-flex items-center gap-2 rounded-full ring-1 ring-white/20 px-6 py-3 font-medium text-paper hover:ring-lime hover:text-lime transition-colors"
            >
              What it guarantees
            </Link>
          </div>
        </div>

        <div className="space-y-3 min-w-0">
          {layerGlance.map((l) => (
            <div
              key={l.n}
              className={`flex items-start gap-4 rounded-lg p-4 ring-1 ${
                l.accent ? 'bg-amber/10 ring-amber/30' : 'bg-white/[0.04] ring-white/10'
              }`}
            >
              <Index n={l.n} tone="dark" accent={l.accent} />
              <div className="min-w-0">
                <p className="font-medium text-paper text-[15px]">{l.name}</p>
                <p className="mt-0.5 text-paper/45 text-[13px] leading-snug lg:truncate">{l.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>

    {/* ---------- Process ---------- */}
    <Section tone="paper">
      <Head
        eyebrow="Grow · harvest · deliver"
        title="Six hours from the tray to your table"
        center
      />
      <div className="mt-14 grid md:grid-cols-3 gap-8">
        {processSteps.map((p, i) => (
          <div key={p.step} className="text-center">
            <img
              src={p.image}
              alt={p.step}
              className="w-40 h-40 rounded-full object-cover mx-auto ring-4 ring-paper2"
            />
            <div className="mt-6 flex items-center justify-center gap-3">
              <Index n={i + 1} />
              <h3 className="font-display font-semibold text-[1.35rem] text-ink">{p.step}</h3>
            </div>
            <p className="mt-3 text-soil/70 text-[14.5px] leading-relaxed max-w-xs mx-auto">
              {p.description}
            </p>
          </div>
        ))}
      </div>
    </Section>

    {/* ---------- Closing CTA ---------- */}
    <Section tone="soil">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <img
          src={kitchenTable}
          alt="A week's harvest laid out on a member's kitchen table"
          className="rounded-xl w-full aspect-[16/10] object-cover ring-1 ring-white/10"
        />
        <div>
          <Head
            tone="dark"
            eyebrow="Membership"
            title="Grown for 500 families, and no more"
            lead="Enrollment is capped so that care, delivery time and quality never have to be traded against volume. When the seats are filled, it closes until one opens."
          />
          <div className="mt-9 flex flex-wrap gap-3">
            <Link
              to="/membership"
              className="inline-flex items-center gap-2 rounded-full bg-lime text-ink px-7 py-3.5 font-semibold hover:bg-paper transition-colors"
            >
              Request an invitation <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full ring-1 ring-white/20 px-7 py-3.5 font-medium text-paper hover:ring-lime hover:text-lime transition-colors"
            >
              Talk to us
            </Link>
          </div>
        </div>
      </div>
    </Section>
  </div>
);

export default Home;
